import {
  animationEnterList,
  animationExitList,
  animationList,
} from '@modules/panel/PanelComponentLayer/animationList';
import { $sceneMapApi } from '@stores/dataStores';
import {
  $editingComponentLayerApi,
  onLayerAnimationPreview,
} from '@stores/editingComponentLayerStores';
import {
  type ComponentLayer,
  type ComponentLayerAnimationCategory,
  type GeneralID,
  MediaType,
} from '@types';
import cn from '@utils/cn';
import getImageSize from '@utils/getImageSize';
import { getUrlByMediaType } from '@utils/urlMakers';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'styled-components';
import { onComponentLayerClick } from '../actions';

interface LayerItemProps {
  data: ComponentLayer;
  sceneId: GeneralID;
  editingTab: ComponentLayerAnimationCategory | null;
  isBatchSelected: boolean;
  type: 'mobile' | 'desktop';
}

export function LayerItem({
  data,
  sceneId,
  editingTab,
  isBatchSelected,
  type,
}: LayerItemProps) {
  const {
    icon,
    size: sizeMap,
    visible,
    id,
    bgColor,
    displayMode,
    locked,
    enterAnimation,
    animation,
    exitAnimation,
  } = data;
  const size = sizeMap[type];
  const pos = icon[type];
  const src = getUrlByMediaType(
    icon.mediaType === 0 ? MediaType.system : MediaType.lib,
    icon.mediaUrl
  );
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0,
    ratio: 1,
    loaded: false,
  });
  const startPos = useRef({ x: 0, y: 0 });
  const startOffset = useRef({ x: 0, y: 0 });
  const isDragAction = useRef(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imgEl, setImgEl] = useState({ width: 0, height: 0 });
  const [animationKey, setAnimationKey] = useState(0);
  const isEditing = editingTab !== null;

  useEffect(() => {
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    setImageSize((prev) => ({
      ...prev,
      loaded: false,
    }));
    // 组件加载时，获取图片的宽度、高度、宽高比
    if (icon.mediaUrl) {
      getImageSize(getUrlByMediaType(icon.mediaType, icon.mediaUrl)).then(
        (srcSize) => {
          if (srcSize) {
            setImageSize({
              width: srcSize.width,
              height: srcSize.height,
              ratio: srcSize.width / srcSize.height,
              loaded: true,
            });
          }
        }
      );
    }
  }, [icon.mediaUrl]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const { width, height } = entry.contentRect;
        setImgEl({ width, height });
      });
    });
    if (imageRef.current) {
      resizeObserver.observe(imageRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [imageSize.loaded, animationKey]);

  useEffect(() => {
    setAnimationKey((prev) => prev + 1);
  }, [animation, enterAnimation, exitAnimation, editingTab]);

  useEffect(() => {
    if (!isEditing) return;
    return onLayerAnimationPreview.watch(() => {
      setAnimationKey((prev) => prev + 1);
    });
  }, [isEditing]);

  if (!imageSize.loaded) return null;

  // 根据图层模式、宽高比锁定状态和自适应依据计算宽度和高度
  let width: string;
  let height: string;
  if (size.type === 0) {
    if (size.adaptiveBy === 0) {
      width = `${size.width}px`;
      height = size.lockAspectRatio
        ? `${size.width / imageSize.ratio}px`
        : `${size.height}px`;
    } else {
      height = `${size.height}px`;
      width = size.lockAspectRatio
        ? `${size.height * imageSize.ratio}px`
        : `${size.width}px`;
    }
  } else if (size.adaptiveBy === 0) {
    width = `${size.width * 100}%`;
    height = size.lockAspectRatio
      ? `${imgEl.width / imageSize.ratio}px`
      : `${size.height * 100}%`;
  } else {
    height = `${size.height * 100}%`;
    width = size.lockAspectRatio
      ? `${imgEl.height * imageSize.ratio}px`
      : `${size.width * 100}%`;
  }

  // 根据edge计算位置
  let position: {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
    transform?: string;
  } = {};
  switch (pos.edge) {
    case 'center':
      position = {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
      break;
    case 'leftTop':
      position = { top: '0px', left: '0px' };
      break;
    case 'rightTop':
      position = { top: '0px', right: '0px' };
      break;
    case 'topCenter':
      position = { top: '0px', left: '50%', transform: 'translateX(-50%)' };
      break;
    case 'leftCenter':
      position = { top: '50%', left: '0px', transform: 'translateY(-50%)' };
      break;
    case 'rightCenter':
      position = { top: '50%', right: '0px', transform: 'translateY(-50%)' };
      break;
    case 'leftBottom':
      position = { bottom: '0px', left: '0px' };
      break;
    case 'rightBottom':
      position = { bottom: '0px', right: '0px' };
      break;
    case 'bottomCenter':
      position = { bottom: '0px', left: '50%', transform: 'translateX(-50%)' };
      break;
    default:
      break;
  }

  position = {
    ...position,
    top: position.top ? `calc(${position.top} + ${pos.offsetY}px)` : undefined,
    left: position.left
      ? `calc(${position.left} + ${pos.offsetX}px)`
      : undefined,
    right: position.right
      ? `calc(${position.right} - ${pos.offsetX}px)`
      : undefined,
    bottom: position.bottom
      ? `calc(${position.bottom} - ${pos.offsetY}px)`
      : undefined,
  };

  /**
   * 当特定动画类型是, 需要隐藏溢出部分
   */
  const getIsOverflowHide = () => {
    if ([22, 23, 24, 25].includes(enterAnimation?.type ?? 0)) return true;
    if ([22, 23, 24, 25].includes(exitAnimation?.type ?? 0)) return true;
    return false;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    startOffset.current = { x: pos.offsetX, y: pos.offsetY };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    isDragAction.current = false;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const movedX = e.clientX - startPos.current.x;
    const movedY = e.clientY - startPos.current.y;
    // 只有当拖动超过阈值时才判断为拖动
    if (Math.abs(movedX) > 2 || Math.abs(movedY) > 2) {
      isDragAction.current = true;
      // 锁定时不移动
      if (locked) return;
      if (isEditing) {
        $editingComponentLayerApi.update((draft) => {
          const editingPos = draft.icon[type];
          editingPos.offsetX = startOffset.current.x + movedX;
          editingPos.offsetY = startOffset.current.y + movedY;
        });
      }
      $sceneMapApi.updateScene({
        id: sceneId,
        updater: (draft) => {
          const layer = draft.config.components?.find((x) => x.id === id);
          if (layer) {
            const itemPos = layer.icon[type];
            itemPos.offsetX = startOffset.current.x + movedX;
            itemPos.offsetY = startOffset.current.y + movedY;
          }
        },
      });
    }
  };

  const handleMouseUp = () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const getAnimationStyle = (): CSSProperties => {
    const list: {
      name: string;
      duration: number;
      count: number | 'infinite';
      delay: number;
    }[] = [];
    // 当处于编辑状态时, 只播放当前编辑的部分的动画('进入'、'停留'、'退出')
    // 不处于编辑状态时, 播放`进入` + `停留`动画. 如果停留后需要进入隐藏, 则需要加上`退出`动画
    // 通过`editingTab`判断把哪些动画加入到`list`中

    // 添加退出动画到列表
    function addExitAnimationToList({
      curDelay,
      curList,
    }: {
      curDelay: number;
      curList: typeof list;
    }) {
      if (exitAnimation && exitAnimation.type > 0) {
        curList.push({
          name:
            animationExitList.find((x) => x.id === exitAnimation.type)
              ?.animationName ?? '',
          duration: exitAnimation.duration,
          count: 1,
          delay: curDelay + exitAnimation.delay,
        });
      }
    }

    let prevDelay = 0;

    // 进入动画部分
    if (
      enterAnimation &&
      enterAnimation.type > 0 &&
      (editingTab === null || editingTab === 'enterAnimation')
    ) {
      list.push({
        name: 'hide',
        duration: enterAnimation.delay,
        count: 1,
        delay: 0,
      });
      list.push({
        name:
          animationEnterList.find((x) => x.id === enterAnimation.type)
            ?.animationName ?? '',
        duration: enterAnimation.duration,
        count: 1,
        delay: enterAnimation.delay,
      });
      prevDelay = enterAnimation.delay + enterAnimation.duration;
    }

    // 停留动画部分
    if (animation && (editingTab === null || editingTab === 'animation')) {
      // 无动画 - 停留一定时间后隐藏
      if (animation.type === 0 && animation.stayMode === 1) {
        const stayDuration = animation.stayDuration ?? 5;
        // 如果是编辑中, 停留时间结束后变为半透明
        if (editingTab === 'animation') {
          list.push({
            name: 'hide-editing',
            duration: 0,
            count: 1,
            delay: stayDuration,
          });
        }
        // 如果不是编辑中, 有结束动画, 则在结束动画后再播放退出动画, 没有则隐藏
        if (editingTab === null) {
          if (exitAnimation && exitAnimation.type > 0) {
            addExitAnimationToList({
              curDelay: prevDelay + stayDuration,
              curList: list,
            });
          } else {
            list.push({
              name: 'hide',
              duration: 0,
              count: 1,
              delay: prevDelay + stayDuration,
            });
          }
        }
      }

      // 有动画
      if (animation.type > 0) {
        let delay =
          editingTab === 'animation'
            ? animation.delay
            : prevDelay + animation.delay;
        list.push({
          name:
            animationList.find((x) => x.id === animation.type)?.animationName ??
            '',
          duration: animation.duration,
          count: animation.loopCount > 0 ? animation.loopCount : 'infinite',
          delay,
        });

        // 动画播放方式 - 播放后隐藏
        if (animation.loopCount !== 0 && animation.playEndAction === 1) {
          delay += animation.duration * animation.loopCount;
          // 如果是编辑中, 停留时间结束后变为半透明
          if (editingTab === 'animation') {
            list.push({
              name: 'hide-editing',
              duration: 0,
              count: 1,
              delay: delay,
            });
          }
          // 如果不是编辑中, 有结束动画, 则在结束动画后再播放退出动画, 没有则隐藏
          if (editingTab === null) {
            if (exitAnimation && exitAnimation.type > 0) {
              addExitAnimationToList({
                curDelay: delay,
                curList: list,
              });
            } else {
              list.push({
                name: 'hide',
                duration: 0,
                count: 1,
                delay: delay,
              });
            }
          }
        }
      }
    }

    // 退出动画部分
    if (editingTab === 'exitAnimation') {
      addExitAnimationToList({
        curDelay: 0,
        curList: list,
      });
    }

    if (!list.length) return {};

    return {
      animationName: list.map((x) => x.name).join(','),
      animationDuration: list.map((x) => `${x.duration}s`).join(','),
      animationTimingFunction: 'linear',
      animationDelay: list.map((x) => `${x.delay}s`).join(','),
      animationIterationCount: list.map((x) => x.count).join(','),
      animationDirection: 'normal',
      animationFillMode: Array(list.length - 1)
        .fill('none')
        .concat('forwards')
        .join(','),
    };
  };

  return (
    <>
      {isEditing && displayMode === 1 && (
        <div
          className="pointer-events-auto absolute inset-0"
          style={{
            backgroundColor: `rgba(${bgColor})`,
          }}
        />
      )}
      <div
        className={cn('pointer-events-auto absolute ', {
          'outline outline-2 outline-orange': isEditing || isBatchSelected,
          'opacity-50': isEditing && visible === false,
          'z-10': isEditing,
          'overflow-hidden': getIsOverflowHide(),
        })}
        style={{ width, height, ...position, perspective: '300px' }}
        onMouseDown={handleMouseDown}
        onClick={() => {
          if (!isDragAction.current) {
            onComponentLayerClick({ id });
          }
        }}
      >
        <img
          // 动画数据变化时，重置动画
          key={animationKey}
          alt=""
          draggable={false}
          src={src}
          className="h-full w-full"
          style={getAnimationStyle()}
          ref={imageRef}
        />
      </div>
    </>
  );
}

export default LayerItem;
